import axios, { AxiosError } from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';

// In development, use relative URL to leverage Vite's proxy (avoids CORS)
// In production, use the configured API URL
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:8000');

/** Absolute or same-origin path to proxied HTML preview for a document's stored source_url. */
export function sourcePreviewUrl(documentId: string): string {
  const base = API_URL.replace(/\/$/, '');
  const path = `/api/documents/${documentId}/source-preview`;
  if (!base) return path;
  return `${base}${path}`;
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Cognito token
api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    // No session or error fetching session - continue without token
    console.debug('No auth session available');
  }
  
  return config;
});

// 401 redirect: only send to login when session is clearly invalid (avoid redirect loops and background noise)
const authPages = ['/login', '/signup', '/activate', '/forgot-password', '/reset-password'];
let last401Redirect = 0;
const REDIRECT_DEBOUNCE_MS = 2000;

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      const isAuthPage = authPages.some((p) => path.startsWith(p));
      const now = Date.now();
      if (!isAuthPage && now - last401Redirect > REDIRECT_DEBOUNCE_MS) {
        last401Redirect = now;
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export interface OntologyTagRef {
  id: string;
  slug: string;
  label: string;
  code?: string | null;
  path: string;
  root_code?: string | null;
}

export interface DocumentListItem {
  id: string;
  title: string;
  source_url?: string;
  category: string;
  tags?: OntologyTagRef[];
  created_at: string;
  updated_at?: string;
  last_crawled_at?: string;
}

export interface Document extends DocumentListItem {
  content?: string;
  meta_data?: Record<string, unknown>;
}

export type ContextType = 'document' | 'previous_chat' | 'upload' | 'project';

export interface ContextItem {
  context_type: ContextType;
  document_id?: string;  // UUID as string
  referenced_session_id?: string;  // UUID as string
  project_id?: string;  // UUID as string
  file_name?: string;
  file_url?: string;
  file_metadata?: Record<string, unknown>;
}

export interface ContextItemResponse extends ContextItem {
  id: string;  // UUID as string
  created_at: string;
}

export interface ChatMessage {
  id: string;  // UUID as string
  role: string;
  content: string;
  created_at: string;
  document_id?: string;  // UUID as string
  context_items?: ContextItemResponse[];
  citations?: RAGCitation[] | null;  // RAG citations (assistant messages only)
}

export interface PaginatedDocumentsResponse {
  items: DocumentListItem[];
  total: number;
  limit: number;
  offset: number;
}

export const documentApi = {
  getDocuments: async (category?: string, tagId?: string, limit = 20, offset = 0): Promise<DocumentListItem[]> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (tagId !== undefined) params.append('tag_id', tagId);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    
    const response = await api.get(`/api/documents/?${params.toString()}`);
    return response.data;
  },

  getDocument: async (id: string): Promise<Document> => {
    const response = await api.get(`/api/documents/${id}`);
    return response.data;
  },

  searchDocuments: async (params: {
    query: string;
    category?: string;
    tagId?: string;
    limit?: number;
    offset?: number;
  }) => {
    const { query, category, tagId, limit = 50, offset = 0 } = params;
    const body: Record<string, unknown> = {
      query,
      limit,
      offset,
    };
    if (category) body.category = category;
    if (tagId !== undefined) body.tag_id = tagId;
    const response = await api.post('/api/documents/search', body);
    return response.data;
  },
};

export type LibraryQuestionChannel = 'staff' | 'community';
export type LibraryQuestionStatus = 'open' | 'answered';

export interface LibraryAnswer {
  id: string;
  question_id: string;
  author_id: string;
  author_name?: string | null;
  body: string;
  created_at: string;
  is_accepted: boolean;
  score: number;
  my_vote: number;
}

export interface LibraryAnswerFeedItem extends LibraryAnswer {
  question_body: string;
}

export interface LibraryQuestion {
  id: string;
  author_id: string;
  author_name?: string | null;
  channel: LibraryQuestionChannel;
  body: string;
  status: LibraryQuestionStatus;
  created_at: string;
  updated_at?: string | null;
  answer_count: number;
}

export interface LibraryQuestionDetail extends LibraryQuestion {
  answers: LibraryAnswer[];
}

export interface LibraryQuestionListResponse {
  items: LibraryQuestion[];
  total: number;
}

export const libraryQuestionsApi = {
  list: async (params: {
    channel?: LibraryQuestionChannel;
    status?: LibraryQuestionStatus;
    q?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<LibraryQuestionListResponse> => {
    const search = new URLSearchParams();
    if (params.channel) search.set('channel', params.channel);
    if (params.status) search.set('status', params.status);
    if (params.q) search.set('q', params.q);
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.offset != null) search.set('offset', String(params.offset));
    const query = search.toString();
    const response = await api.get(`/api/library/questions${query ? `?${query}` : ''}`);
    return response.data;
  },

  get: async (id: string): Promise<LibraryQuestionDetail> => {
    const response = await api.get(`/api/library/questions/${id}`);
    return response.data;
  },

  create: async (payload: { channel: LibraryQuestionChannel; body: string }): Promise<LibraryQuestion> => {
    const response = await api.post('/api/library/questions', payload);
    return response.data;
  },

  answer: async (id: string, body: string): Promise<LibraryQuestionDetail> => {
    const response = await api.post(`/api/library/questions/${id}/answers`, { body });
    return response.data;
  },

  listAnswers: async (params: { limit?: number; offset?: number } = {}): Promise<{
    items: LibraryAnswerFeedItem[];
    total: number;
  }> => {
    const search = new URLSearchParams();
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.offset != null) search.set('offset', String(params.offset));
    const query = search.toString();
    const response = await api.get(`/api/library/questions/answers${query ? `?${query}` : ''}`);
    return response.data;
  },

  voteAnswer: async (questionId: string, answerId: string, direction: 1 | -1): Promise<LibraryAnswer> => {
    const response = await api.post(
      `/api/library/questions/${questionId}/answers/${answerId}/vote`,
      { direction },
    );
    return response.data;
  },

  acceptAnswer: async (questionId: string, answerId: string): Promise<LibraryQuestionDetail> => {
    const response = await api.post(`/api/library/questions/${questionId}/answers/${answerId}/accept`);
    return response.data;
  },
};

export const chatApi = {
  getMessages: async (sessionId: string): Promise<{ messages: ChatMessage[]; references: Document[] }> => {
    const response = await api.get(`/api/chat/sessions/${sessionId}/messages`);
    return response.data;
  },

  getSessions: async (): Promise<ChatSessionListItem[]> => {
    const response = await api.get('/api/chat/sessions');
    return response.data;
  },

  updateSession: async (sessionId: string, data: { project_id?: string | null }): Promise<ChatSessionListItem> => {
    const response = await api.patch(`/api/chat/sessions/${sessionId}`, data);
    return response.data;
  },

  getSharedWith: async (sessionId: string): Promise<SharedUser[]> => {
    const response = await api.get(`/api/chat/sessions/${sessionId}/shared-with`);
    return response.data;
  },

  addSharedWith: async (sessionId: string, userId: string, role: 'viewer' | 'editor'): Promise<void> => {
    await api.post(`/api/chat/sessions/${sessionId}/shared-with`, { user_id: userId, role });
  },

  updateSharedWithRole: async (sessionId: string, userId: string, role: 'viewer' | 'editor'): Promise<void> => {
    await api.patch(`/api/chat/sessions/${sessionId}/shared-with/${userId}`, { role });
  },

  removeSharedWith: async (sessionId: string, userId: string): Promise<void> => {
    await api.delete(`/api/chat/sessions/${sessionId}/shared-with/${userId}`);
  },
};

export interface SharedUser {
  user_id: string;  // UUID as string
  username: string;
  email: string;
  full_name?: string;
  role: 'viewer' | 'editor';
  created_at: string;
}

export interface ChatSessionListItem {
  id: string;  // UUID as string
  title: string;
  created_at: string;
  updated_at: string | null;
  project_ids?: string[];
  is_owner?: boolean;
  role?: 'owner' | 'editor' | 'viewer';
}

export const resourcesApi = {
  getGovernmentRegulatory: async (limit = 20, subType?: string): Promise<DocumentListItem[]> => {
    const page = await resourcesApi.getGovernmentRegulatoryPage(limit, subType, 0);
    return page.items;
  },

  getGovernmentRegulatoryPage: async (limit = 20, subType?: string, offset = 0): Promise<PaginatedDocumentsResponse> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (subType) params.set('tag', subType);
    params.set('offset', String(offset));
    const response = await api.get(`/api/resources/government-regulatory?${params}`);
    return response.data;
  },

  getCodingClassification: async (limit = 20, subType?: string): Promise<DocumentListItem[]> => {
    const page = await resourcesApi.getCodingClassificationPage(limit, subType, 0);
    return page.items;
  },

  getCodingClassificationPage: async (limit = 20, subType?: string, offset = 0): Promise<PaginatedDocumentsResponse> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (subType) params.set('tag', subType);
    params.set('offset', String(offset));
    const response = await api.get(`/api/resources/coding-classification?${params}`);
    return response.data;
  },

  getHospitalAdvocacy: async (limit = 20, subType?: string): Promise<DocumentListItem[]> => {
    const page = await resourcesApi.getHospitalAdvocacyPage(limit, subType, 0);
    return page.items;
  },

  getHospitalAdvocacyPage: async (limit = 20, subType?: string, offset = 0): Promise<PaginatedDocumentsResponse> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (subType) params.set('tag', subType);
    params.set('offset', String(offset));
    const response = await api.get(`/api/resources/hospital-advocacy?${params}`);
    return response.data;
  },

  getPayerPolicies: async (limit = 20, subType?: string): Promise<DocumentListItem[]> => {
    const page = await resourcesApi.getPayerPoliciesPage(limit, subType, 0);
    return page.items;
  },

  getPayerPoliciesPage: async (limit = 20, subType?: string, offset = 0): Promise<PaginatedDocumentsResponse> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (subType) params.set('tag', subType);
    params.set('offset', String(offset));
    const response = await api.get(`/api/resources/payer-policies?${params}`);
    return response.data;
  },

  getDenialsAppeals: async (limit = 20, subType?: string): Promise<DocumentListItem[]> => {
    const page = await resourcesApi.getDenialsAppealsPage(limit, subType, 0);
    return page.items;
  },

  getDenialsAppealsPage: async (limit = 20, subType?: string, offset = 0): Promise<PaginatedDocumentsResponse> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (subType) params.set('tag', subType);
    params.set('offset', String(offset));
    const response = await api.get(`/api/resources/denials-appeals?${params}`);
    return response.data;
  },

  getTechnologyBenchmarks: async (limit = 20, subType?: string): Promise<DocumentListItem[]> => {
    const page = await resourcesApi.getTechnologyBenchmarksPage(limit, subType, 0);
    return page.items;
  },

  getTechnologyBenchmarksPage: async (limit = 20, subType?: string, offset = 0): Promise<PaginatedDocumentsResponse> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (subType) params.set('tag', subType);
    params.set('offset', String(offset));
    const response = await api.get(`/api/resources/technology-benchmarks?${params}`);
    return response.data;
  },

  getIndustryNews: async (limit = 20, subType?: string): Promise<DocumentListItem[]> => {
    const page = await resourcesApi.getIndustryNewsPage(limit, subType, 0);
    return page.items;
  },

  getIndustryNewsPage: async (limit = 20, subType?: string, offset = 0): Promise<PaginatedDocumentsResponse> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (subType) params.set('tag', subType);
    params.set('offset', String(offset));
    const response = await api.get(`/api/resources/industry-news?${params}`);
    return response.data;
  },
};

// Auth types and API
export interface User {
  id: string;  // UUID as string
  email: string;
  username: string;
  full_name?: string;
  state?: string;
  organization?: string;
  organization_type?: string;
  title?: string;
  profile_completed: boolean;
  is_active: boolean;
  email_verified: boolean;
  is_admin: boolean;
  created_at: string;
}

export interface UserPreferences {
  id: string;
  user_id: string;
  care_settings: string[];
  billing_types: string[];
  rcm_departments: string[];
  payer_focus: string[];
  interest_areas: string[];
  role_level?: string;
  primary_state?: string;
  mac_region?: string;
  additional_states: string[];
  created_at: string;
  updated_at?: string;
}

export type UserPreferencesUpdate = Partial<Omit<UserPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'>>;

export interface CognitoConfig {
  region: string;
  userPoolId: string;
  userPoolClientId: string;
}

export const authApi = {
  // Get Cognito configuration from backend
  getCognitoConfig: async (): Promise<CognitoConfig> => {
    const response = await api.get<CognitoConfig>('/api/auth/cognito-config');
    return response.data;
  },

  // Get current user (from Cognito token)
  me: async (): Promise<User> => {
    const response = await api.get<User>('/api/auth/me');
    return response.data;
  },

  // Update profile
  updateProfile: async (data: {
    full_name?: string;
    organization?: string;
    organization_type?: string;
    title?: string;
    profile_completed?: boolean;
  }): Promise<User> => {
    const response = await api.patch<User>('/api/auth/me', data);
    return response.data;
  },

  // Get user preferences
  getPreferences: async (): Promise<UserPreferences> => {
    const response = await api.get<UserPreferences>('/api/auth/preferences');
    return response.data;
  },

  // Update user preferences
  updatePreferences: async (data: UserPreferencesUpdate): Promise<UserPreferences> => {
    const response = await api.patch<UserPreferences>('/api/auth/preferences', data);
    return response.data;
  },

  // Delete account (backend only - Cognito user should be deleted from frontend)
  deleteAccount: async (): Promise<void> => {
    await api.post('/api/auth/delete-account', { confirm: true });
  },
};

export interface Project {
  id: string;  // UUID as string
  name: string;
  description?: string;
  created_at: string;
  updated_at?: string;
}

export type ProjectRole = 'owner' | 'editor' | 'viewer';

export interface ProjectWithRole extends Project {
  current_user_role: ProjectRole;
}

export interface ProjectSharedWithUser {
  id: string;  // UUID as string
  username: string;
  full_name?: string | null;
  role: 'editor' | 'viewer';
}

export interface ProjectResourcesSummary {
  documents: DocumentListItem[];
  chats: ProjectChatItem[];
  files: ProjectFileItem[];
}

export const projectsApi = {
  list: async (): Promise<Project[]> => {
    const response = await api.get('/api/projects/');
    return response.data;
  },

  listShared: async (): Promise<Project[]> => {
    const response = await api.get('/api/projects/shared');
    return response.data;
  },

  getResourcesSummary: async (): Promise<ProjectResourcesSummary> => {
    const response = await api.get<ProjectResourcesSummary>('/api/projects/resources-summary');
    return response.data;
  },

  get: async (id: string): Promise<ProjectWithRole> => {
    const response = await api.get(`/api/projects/${id}`);
    return response.data;
  },

  create: async (data: { name: string; description?: string }): Promise<Project> => {
    const response = await api.post('/api/projects/', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; description?: string }): Promise<Project> => {
    const response = await api.put(`/api/projects/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/projects/${id}`);
  },

  getDocuments: async (projectId: string): Promise<DocumentListItem[]> => {
    const response = await api.get(`/api/projects/${projectId}/documents`);
    return response.data;
  },

  addDocument: async (projectId: string, documentId: string): Promise<void> => {
    await api.post(`/api/projects/${projectId}/documents`, { document_id: documentId });
  },

  removeDocument: async (projectId: string, documentId: string): Promise<void> => {
    await api.delete(`/api/projects/${projectId}/documents/${documentId}`);
  },

  getSharedWith: async (projectId: string): Promise<ProjectSharedWithUser[]> => {
    const response = await api.get(`/api/projects/${projectId}/shared-with`);
    return response.data;
  },

  addSharedWith: async (projectId: string, userId: string, role: 'editor' | 'viewer' = 'viewer'): Promise<void> => {
    await api.post(`/api/projects/${projectId}/shared-with`, { user_id: userId, role });
  },

  updateSharedWithRole: async (projectId: string, userId: string, role: 'editor' | 'viewer'): Promise<void> => {
    await api.patch(`/api/projects/${projectId}/shared-with/${userId}`, { role });
  },

  removeSharedWith: async (projectId: string, userId: string): Promise<void> => {
    await api.delete(`/api/projects/${projectId}/shared-with/${userId}`);
  },

  getChats: async (projectId: string): Promise<ProjectChatItem[]> => {
    const response = await api.get(`/api/projects/${projectId}/chats`);
    return response.data;
  },

  addChat: async (projectId: string, chatId: string): Promise<void> => {
    await api.post(`/api/projects/${projectId}/chats`, { chat_id: chatId });
  },

  removeChat: async (projectId: string, chatId: string): Promise<void> => {
    await api.delete(`/api/projects/${projectId}/chats/${chatId}`);
  },

  getFiles: async (projectId: string): Promise<ProjectFileItem[]> => {
    const response = await api.get(`/api/projects/${projectId}/files`);
    return response.data;
  },

  uploadFile: async (projectId: string, file: File): Promise<ProjectFileItem> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/projects/${projectId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  removeFile: async (projectId: string, fileId: string): Promise<void> => {
    await api.delete(`/api/projects/${projectId}/files/${fileId}`);
  },
};

export interface ProjectFileItem {
  id: string;  // UUID as string
  file_name: string;
  file_size: number;
  created_at: string;
  item_type: 'pdf';
}

export interface ProjectChatItem {
  id: string;  // UUID as string
  title: string | null;
  created_at: string;
  updated_at: string | null;
  is_owner?: boolean;
  role?: string;
  item_type: 'chat';
}

export interface UserSearchResult {
  id: string;  // UUID as string
  username: string;
  full_name?: string | null;
}

export const usersApi = {
  search: async (q: string): Promise<UserSearchResult[]> => {
    const response = await api.get('/api/users/search', { params: { q: q.trim() } });
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// RAG API
// ---------------------------------------------------------------------------

export interface RAGCitation {
  index: number;
  document_id: string;
  title: string;
  payer: string;
  document_type: string;
  url: string;
  score: number;
}

export interface RAGChatResponse {
  answer: string;
  citations: RAGCitation[];
  model: string;
}

export interface RAGUploadResponse {
  document_id: string;
  collection: string;
  chunks_written: number;
  success: boolean;
  error?: string;
}

export type RAGStreamChunk = string;  // raw text delta
export type RAGStreamCitations = { citations: RAGCitation[] };
export type RAGStreamDone = { done: true };

export const ragApi = {
  /**
   * Streaming RAG chat. Yields session_id, text chunks, then citations, then done.
   *
   * Streaming is the only supported chat surface — the non-streaming
   * /api/rag/chat endpoint was removed when measurement showed it gave no
   * latency benefit and the chat UI never used it. See the deployed eval
   * analysis at runs/post-annotation-deployed-2026-06-06/analysis.md
   * (Performance Fix 5).
   */
  chatStream: async function* (
    question: string,
    options?: {
      payer?: string;
      document_type?: string;
      chat_session_id?: string;
      upload_ids?: string[];
      document_ids?: string[];
      project_ids?: string[];
      referenced_session_ids?: string[];
      context_mode?: 'auto' | 'selected' | 'all';
      top_k?: number;
      /**
       * Persona shapes the generator's audience register
       * (acronym expansion for beneficiary, code-specifics for coder,
       * etc.). See backend/routers/rag.py RAGChatRequest.user_persona.
       * Recognized values: beneficiary | biller | coder | case_manager | collections.
       */
      user_persona?: string;
    }
  ): AsyncGenerator<
    {
      text?: string;
      citations?: RAGCitation[];
      session_id?: string;
      done?: boolean;
      // 'retrieving' | 'generating' — emitted by the backend so the UI can
      // surface a "Thinking..." spinner before the first token arrives.
      status?: string;
    },
    void,
    unknown
  > {
    const streamApiUrl = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:8000');
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${streamApiUrl}/api/rag/chat/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ question, ...options }),
    });

    if (!res.ok || !res.body) {
      yield { done: true };
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6);
          if (raw === '[DONE]') { yield { done: true }; return; }
          if (raw.startsWith('[SESSION] ')) {
            yield { session_id: raw.slice(10).trim() };
            continue;
          }
          if (raw.startsWith('[CITATIONS] ')) {
            try {
              const citations: RAGCitation[] = JSON.parse(raw.slice(12));
              yield { citations };
            } catch { /* ignore */ }
            continue;
          }
          if (raw.startsWith('[STATUS] ')) {
            // Progress hint (retrieving / generating) so the chat hook can
            // surface a "Thinking…" indicator before the first token lands.
            // Backwards-compatible: older clients that don't know [STATUS]
            // just ignore the event (the JSON.parse below would throw).
            yield { status: raw.slice(9).trim() };
            continue;
          }
          try {
            const text = JSON.parse(raw);
            if (typeof text === 'string') yield { text };
          } catch { /* ignore */ }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  /** Upload a file and ingest it into the user's Qdrant collection. */
  upload: async (file: File, documentId?: string): Promise<RAGUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (documentId) formData.append('document_id', documentId);
    const response = await api.post('/api/rag/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /** Health check for RAG pipeline components. */
  health: async (): Promise<Record<string, unknown>> => {
    const response = await api.get('/api/rag/health');
    return response.data;
  },

  /** Get persisted context items for a session (uploads, documents, projects). */
  getSessionContext: async (sessionId: string): Promise<{
    uploads: { id: string; document_id: string | null; file_name: string | null; file_metadata: Record<string, unknown> }[];
    documents: { id: string; document_id: string | null }[];
    projects: { id: string; project_id: string | null }[];
    previous_chats: { id: string; referenced_session_id: string | null; title?: string | null }[];
  }> => {
    const response = await api.get(`/api/rag/sessions/${sessionId}/context`);
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Ontology (facet dimensions: source, subject, rcm-cycle, doc-kind, program)
// ---------------------------------------------------------------------------

export interface OntologyNodeItem {
  id: string;
  label: string;
  slug?: string;
  /** Stable public key (eval / crawler hint); formerly topics.api_value */
  code?: string;
  children?: OntologyNodeItem[];
}

/** @deprecated Use OntologyNodeItem */
export type TopicItem = OntologyNodeItem;

export interface OntologyTreeGrouped {
  /** Roots keyed by dimension slug (e.g. subject, source). */
  byDimension: Record<string, OntologyNodeItem[]>;
  dimensions: OntologyDimensionResponse[];
}

/** @deprecated Use OntologyTreeGrouped */
export type TopicsGrouped = OntologyTreeGrouped;

export interface OntologyNodeResponse {
  id: string;
  dimension_id: string;
  parent_id?: string | null;
  slug: string;
  path: string;
  depth: number;
  label: string;
  description?: string | null;
  classification_description?: string | null;
  include_criteria?: string | null;
  exclude_criteria?: string | null;
  code?: string | null;
  sort_order: number;
  is_active: boolean;
  children?: OntologyNodeResponse[] | null;
}

export interface OntologyDimensionResponse {
  id: string;
  slug: string;
  label: string;
  description?: string | null;
  selection_mode: string;
  sort_order: number;
  roots: OntologyNodeResponse[];
}

export interface OntologyTreeResponse {
  dimensions: OntologyDimensionResponse[];
}

function nodeToItem(node: OntologyNodeResponse): OntologyNodeItem {
  return {
    id: node.id,
    label: node.label,
    slug: node.slug,
    code: node.code ?? undefined,
    children: (node.children ?? []).map(nodeToItem),
  };
}

function treeToGrouped(tree: OntologyTreeResponse): OntologyTreeGrouped {
  const byDimension: Record<string, OntologyNodeItem[]> = {};
  for (const dim of tree.dimensions ?? []) {
    byDimension[dim.slug] = (dim.roots ?? []).map(nodeToItem);
  }
  return { byDimension, dimensions: tree.dimensions ?? [] };
}

/** Subject facet roots (Library browse / eval root codes). */
export function subjectRoots(tree: OntologyTreeGrouped | null | undefined): OntologyNodeItem[] {
  return tree?.byDimension?.subject ?? [];
}

export const ontologyApi = {
  getTree: async (): Promise<OntologyTreeGrouped> => {
    const response = await api.get<OntologyTreeResponse>('/api/ontology/tree');
    return treeToGrouped(response.data);
  },
};

/** @deprecated Use ontologyApi */
export const topicsApi = {
  getTopics: ontologyApi.getTree,
};

// ---------------------------------------------------------------------------
// Admin Ontology API
// ---------------------------------------------------------------------------

export type OntologyDimensionSlug = string;

/** @deprecated Use OntologyDimensionSlug */
export type TopicAdminType = OntologyDimensionSlug;

export interface OntologyAdminNode {
  id: string;
  dimension_id: string;
  label: string;
  parent_id: string | null;
  slug: string | null;
  /** Dimension slug */
  dimension_slug: OntologyDimensionSlug;
  code: string | null;
  description?: string | null;
  classification_description?: string | null;
}

/** @deprecated Use OntologyAdminNode */
export type TopicAdminItem = OntologyAdminNode & { type: OntologyDimensionSlug; api_value: string | null };

export interface OntologyNodeCreatePayload {
  label: string;
  dimension_slug: OntologyDimensionSlug;
  parent_id?: string | null;
  slug?: string | null;
  code?: string | null;
}

/** @deprecated Use OntologyNodeCreatePayload */
export interface TopicCreatePayload {
  label: string;
  type: OntologyDimensionSlug;
  parent_id?: string | null;
  slug?: string | null;
  api_value?: string | null;
}

function nodeToAdminItem(node: OntologyNodeResponse, dimSlug: string): TopicAdminItem {
  return {
    id: node.id,
    dimension_id: node.dimension_id,
    label: node.label,
    parent_id: node.parent_id ?? null,
    slug: node.slug,
    dimension_slug: dimSlug,
    type: dimSlug,
    code: node.code ?? null,
    api_value: node.code ?? null,
    description: node.description,
    classification_description: node.classification_description,
  };
}

export const adminApi = {
  getDimensions: async (): Promise<OntologyDimensionResponse[]> => {
    const response = await api.get<OntologyDimensionResponse[]>('/api/admin/ontology/dimensions');
    return response.data;
  },

  getNodes: async (): Promise<TopicAdminItem[]> => {
    const [dims, nodes] = await Promise.all([
      api.get<OntologyDimensionResponse[]>('/api/admin/ontology/dimensions'),
      api.get<OntologyNodeResponse[]>('/api/admin/ontology/nodes'),
    ]);
    const slugById = Object.fromEntries(dims.data.map((d) => [d.id, d.slug]));
    return nodes.data.map((n) => nodeToAdminItem(n, slugById[n.dimension_id] ?? ''));
  },

  /** @deprecated Use getNodes */
  getTopics: async (): Promise<TopicAdminItem[]> => adminApi.getNodes(),

  createNode: async (data: OntologyNodeCreatePayload): Promise<TopicAdminItem> => {
    const dims = await adminApi.getDimensions();
    const dim = dims.find((d) => d.slug === data.dimension_slug) ?? dims[0];
    if (!dim) {
      throw new Error('No ontology dimension available');
    }
    const response = await api.post<OntologyNodeResponse>('/api/admin/ontology/nodes', {
      dimension_id: dim.id,
      parent_id: data.parent_id ?? null,
      slug: data.slug,
      label: data.label,
      code: data.code,
    });
    return nodeToAdminItem(response.data, dim.slug);
  },

  /** @deprecated Use createNode */
  createTopic: async (data: TopicCreatePayload): Promise<TopicAdminItem> =>
    adminApi.createNode({
      label: data.label,
      dimension_slug: data.type,
      parent_id: data.parent_id,
      slug: data.slug,
      code: data.api_value,
    }),

  deleteNode: async (id: string): Promise<void> => {
    await api.delete(`/api/admin/ontology/nodes/${id}`);
  },

  /** @deprecated Use deleteNode */
  deleteTopic: async (id: string): Promise<void> => adminApi.deleteNode(id),
};

// ---------------------------------------------------------------------------
// Admin Resources API
// ---------------------------------------------------------------------------

export interface AdminResourceUploadResponse {
  document_id: string;
  chunks_written: number;
  success: boolean;
  error?: string | null;
}

export interface AdminDocumentItem {
  id: string;
  title: string;
  source_url: string | null;
  category: string;
  tags: OntologyTagRef[];
  payer: string | null;
  policy_number: string | null;
  chunk_count: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AdminDocumentListResponse {
  total: number;
  items: AdminDocumentItem[];
}

export interface AdminDocumentPatch {
  title?: string;
  tag_ids?: string[] | null;
  payer?: string | null;
  policy_number?: string | null;
}

export const adminResourcesApi = {
  /** Admin-only: upload a global PDF resource (ingested into knowledge_base). */
  uploadGlobalPdf: async (params: {
    file: File;
    source_url: string;
    title?: string;
    tag_id: string;
    payer?: string;
    policy_number?: string;
  }): Promise<AdminResourceUploadResponse> => {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('source_url', params.source_url);
    if (params.title) formData.append('title', params.title);
    formData.append('tag_id', params.tag_id);
    if (params.payer) formData.append('payer', params.payer);
    if (params.policy_number) formData.append('policy_number', params.policy_number);
    const response = await api.post('/api/admin/resources/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /** List library documents with pagination + filters. */
  listDocuments: async (params: {
    q?: string;
    tag_id?: string;
    category?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<AdminDocumentListResponse> => {
    const response = await api.get<AdminDocumentListResponse>(
      '/api/admin/resources/documents',
      { params },
    );
    return response.data;
  },

  /** Update editable fields on a library document (partial patch). */
  updateDocument: async (
    id: string,
    patch: AdminDocumentPatch,
  ): Promise<AdminDocumentItem> => {
    const response = await api.patch<AdminDocumentItem>(
      `/api/admin/resources/documents/${id}`,
      patch,
    );
    return response.data;
  },

  /** Delete a library document (removes DB row and Qdrant chunks). */
  deleteDocument: async (id: string): Promise<void> => {
    await api.delete(`/api/admin/resources/documents/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Admin Crawler API — queue visibility (#92)
// ---------------------------------------------------------------------------

export interface CrawlerQueueStatus {
  queue_name: string;
  messages_available: number;
  messages_in_flight: number;
}

export interface CrawlerSeedItem {
  id: string;
  url: string;
  domain?: string | null;
  title?: string | null;
  source: 'web_search' | 'manual' | 'import';
  status: 'active' | 'paused';
  notes?: string | null;
  times_cited: number;
  last_cited_at?: string | null;
  last_enqueued_at?: string | null;
  created_at?: string | null;
}

export interface CrawlerSeedImportResult {
  imported: number;
  skipped_existing: number;
  skipped_invalid: number;
}

export const adminCrawlerApi = {
  /** Fetch approximate queue depth for the crawler SQS queue. */
  getQueueStatus: async (): Promise<CrawlerQueueStatus> => {
    const response = await api.get<CrawlerQueueStatus>(
      '/api/admin/crawler/queue-status',
    );
    return response.data;
  },

  // --- Crawler seed list (persisted URLs; web-search fallback captures + manual) ---

  listSeeds: async (params?: {
    search?: string;
    source?: string;
    status?: string;
  }): Promise<CrawlerSeedItem[]> => {
    const response = await api.get<CrawlerSeedItem[]>('/api/admin/crawler/seeds', {
      params,
    });
    return response.data;
  },

  createSeed: async (data: {
    url: string;
    title?: string;
    notes?: string;
    status?: 'active' | 'paused';
  }): Promise<CrawlerSeedItem> => {
    const response = await api.post<CrawlerSeedItem>('/api/admin/crawler/seeds', data);
    return response.data;
  },

  updateSeed: async (
    id: string,
    data: { title?: string; notes?: string; status?: 'active' | 'paused' },
  ): Promise<CrawlerSeedItem> => {
    const response = await api.patch<CrawlerSeedItem>(
      `/api/admin/crawler/seeds/${id}`,
      data,
    );
    return response.data;
  },

  deleteSeed: async (id: string): Promise<void> => {
    await api.delete(`/api/admin/crawler/seeds/${id}`);
  },

  /** Send this seed to the crawler queue now (single-page crawl). */
  enqueueSeed: async (id: string): Promise<CrawlerSeedItem> => {
    const response = await api.post<CrawlerSeedItem>(
      `/api/admin/crawler/seeds/${id}/enqueue`,
    );
    return response.data;
  },

  /** Export all seeds. CSV returns raw text; JSON returns the seed list. */
  exportSeeds: async (format: 'json' | 'csv' = 'json'): Promise<string> => {
    const response = await api.get(`/api/admin/crawler/seeds/export`, {
      params: { format },
      responseType: format === 'csv' ? 'text' : 'json',
    });
    return format === 'csv'
      ? (response.data as string)
      : JSON.stringify(response.data, null, 2);
  },

  /** Bulk import from CSV text (url[,title,notes], optional header row). */
  importSeeds: async (csv: string): Promise<CrawlerSeedImportResult> => {
    const response = await api.post<CrawlerSeedImportResult>(
      '/api/admin/crawler/seeds/import',
      { csv },
    );
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Admin Opinion Articles API — git-backed LLM wiki (dev/projects/opinion-articles/).
// The rcm-opinions-kb repository is the system of record: pasting a source
// exports it as a source/* pull request; synthesis, review, and publishing
// happen there; articles sync back on merge. Raw sources are immutable:
// there are deliberately no update/delete methods, and no publish/delete —
// those are KB-repo pull requests.
// ---------------------------------------------------------------------------

export type OpinionSourceStatus = 'pending_export' | 'pr_open' | 'merged' | 'export_failed';

export type OpinionPageType = 'topic' | 'decision' | 'entity';

export interface OpinionSourceItem {
  id: string;
  title: string;
  source_kind: 'transcript' | 'qa_pairs' | 'notes';
  notes?: string | null;
  content_chars: number;
  status: OpinionSourceStatus;
  raw_path?: string | null;
  pr_number?: number | null;
  pr_url?: string | null;
  error?: string | null;
  exported_at?: string | null;
  created_at?: string | null;
}

export interface OpinionSourceListResponse {
  total: number;
  items: OpinionSourceItem[];
}

export interface OpinionArticleAdminItem {
  id: string;
  document_id: string;
  slug: string;
  page_type: OpinionPageType;
  title: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface OpinionArticleListResponse {
  total: number;
  items: OpinionArticleAdminItem[];
}

export interface OpinionSyncStatus {
  status: string;
  last_synced_sha?: string | null;
  last_synced_at?: string | null;
  error?: string | null;
  report?: Record<string, unknown> | null;
  /** Configured KB repository ("owner/name") — the backend's OPINIONS_KB_REPO. */
  repo?: string | null;
}

export const adminOpinionsApi = {
  createSource: async (data: {
    title: string;
    content: string;
    source_kind: string;
    notes?: string;
  }): Promise<OpinionSourceItem> => {
    const response = await api.post<OpinionSourceItem>('/api/admin/opinions/sources', data);
    return response.data;
  },

  listSources: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<OpinionSourceListResponse> => {
    const response = await api.get<OpinionSourceListResponse>('/api/admin/opinions/sources', {
      params,
    });
    return response.data;
  },

  /** Retry the KB-repo export for a pending_export/export_failed source. */
  exportSource: async (sourceId: string): Promise<OpinionSourceItem> => {
    const response = await api.post<OpinionSourceItem>(
      `/api/admin/opinions/sources/${sourceId}/export`,
    );
    return response.data;
  },

  listArticles: async (params?: {
    page_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<OpinionArticleListResponse> => {
    const response = await api.get<OpinionArticleListResponse>('/api/admin/opinions/articles', {
      params,
    });
    return response.data;
  },

  /** Pull merged KB-repo pages into the app now. full=true rebuilds everything. */
  syncNow: async (full = false): Promise<OpinionSyncStatus> => {
    const response = await api.post<OpinionSyncStatus>('/api/admin/opinions/sync', undefined, {
      params: full ? { full: true } : undefined,
    });
    return response.data;
  },

  getSyncStatus: async (): Promise<OpinionSyncStatus> => {
    const response = await api.get<OpinionSyncStatus>('/api/admin/opinions/sync/status');
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Public Opinion Articles API — user-facing RCM Opinions view
// ---------------------------------------------------------------------------

export interface OpinionArticlePublicItem {
  id: string;
  document_id: string;
  slug: string;
  page_type: OpinionPageType;
  title: string;
  updated_at?: string | null;
}

export interface OpinionArticlePublic extends OpinionArticlePublicItem {
  content: string;
}

export interface OpinionArticlePublicListResponse {
  total: number;
  items: OpinionArticlePublicItem[];
}

export const opinionsApi = {
  list: async (params?: {
    page_type?: OpinionPageType;
    limit?: number;
    offset?: number;
  }): Promise<OpinionArticlePublicListResponse> => {
    const response = await api.get<OpinionArticlePublicListResponse>('/api/opinions/', {
      params,
    });
    return response.data;
  },

  getBySlug: async (slug: string): Promise<OpinionArticlePublic> => {
    const response = await api.get<OpinionArticlePublic>(`/api/opinions/${slug}`);
    return response.data;
  },
};
